import { ExportTheme, PageSize } from "../types";
import { ExportKind, isTauri, saveFileNative } from "./desktop";
import { escapeHtml, slugifyFileName } from "./markdown";

export interface PdfOptions {
  title: string;
  pageSize: PageSize;
  margins: number;
  includePageNumbers: boolean;
}

export interface HtmlOptions {
  title: string;
  theme: ExportTheme;
}

/**
 * Persist export bytes. On desktop (Tauri) the WebView ignores `<a download>`,
 * so route through the native Save dialog + Rust write; in the browser fall back
 * to a blob-URL anchor click. Returns false only when the desktop user cancels.
 */
const deliver = async (data: Uint8Array, filename: string, kind: ExportKind, mime: string): Promise<boolean> => {
  if (isTauri()) {
    return saveFileNative(data, filename, kind);
  }
  const url = URL.createObjectURL(new Blob([data.buffer as ArrayBuffer], { type: mime }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  // Revoke after the click is processed; revoking synchronously can cancel the
  // download in some browsers.
  setTimeout(() => URL.revokeObjectURL(url), 0);
  return true;
};

export const exportMarkdown = (markdown: string, title: string): Promise<boolean> =>
  deliver(new TextEncoder().encode(markdown), `${slugifyFileName(title)}.md`, "md", "text/markdown;charset=utf-8");

export const exportHtml = (html: string, options: HtmlOptions): Promise<boolean> => {
  const dark = options.theme === "dark";
  // Pull in KaTeX styles (CSS + fonts from the CDN) only when the rendered
  // document actually contains math, so plain exports stay dependency-free.
  const katexCss = html.includes("katex")
    ? `\n  <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/katex@0.16/dist/katex.min.css">`
    : "";
  const documentHtml = `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>${escapeHtml(options.title)}</title>${katexCss}
  <style>
    :root { color-scheme: ${dark ? "dark" : "light"}; }
    body {
      margin: 0;
      background: ${dark ? "#111827" : "#f8fafc"};
      color: ${dark ? "#e5e7eb" : "#111827"};
      font: 16px/1.65 Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    }
    main {
      max-width: 860px;
      margin: 0 auto;
      padding: 48px 24px;
    }
    h1, h2, h3 { line-height: 1.2; }
    a { color: ${dark ? "#93c5fd" : "#2563eb"}; }
    img { max-width: 100%; height: auto; border-radius: 8px; }
    blockquote { border-left: 4px solid ${dark ? "#475569" : "#cbd5e1"}; margin-left: 0; padding-left: 16px; color: ${dark ? "#cbd5e1" : "#475569"}; }
    table { border-collapse: collapse; display: block; overflow-x: auto; width: 100%; }
    th, td { border: 1px solid ${dark ? "#374151" : "#d1d5db"}; padding: 8px 10px; }
    code { background: ${dark ? "#1f2937" : "#e5e7eb"}; border-radius: 4px; padding: 2px 5px; }
    pre { background: ${dark ? "#0f172a" : "#f1f5f9"}; border-radius: 8px; overflow: auto; padding: 16px; }
    pre code { background: transparent; padding: 0; }
    hr { border: 0; border-top: 1px solid ${dark ? "#374151" : "#e2e8f0"}; margin: 28px 0; }
  </style>
</head>
<body>
  <main>${html}</main>
</body>
</html>`;

  return deliver(
    new TextEncoder().encode(documentHtml),
    `${slugifyFileName(options.title)}.html`,
    "html",
    "text/html;charset=utf-8",
  );
};

export const exportPdf = async (previewElement: HTMLElement, options: PdfOptions) => {
  const { default: html2pdf } = await import("html2pdf.js");
  const clone = previewElement.cloneNode(true) as HTMLElement;
  clone.classList.add("pdf-export-root");

  const wrapper = document.createElement("section");
  wrapper.className = "pdf-export";
  wrapper.append(clone);

  // Mount the export target in normal flow inside an off-screen stage. html2pdf
  // clones the target into its own container to rasterize it; an out-of-flow
  // (fixed/absolute) target collapses to zero height there, yielding a blank page.
  const stage = document.createElement("section");
  stage.className = "pdf-export-stage";
  stage.setAttribute("aria-hidden", "true");
  stage.append(wrapper);
  document.body.append(stage);

  const filename = `${slugifyFileName(options.title)}.pdf`;
  const marginInches = options.margins / 25.4;

  try {
    await document.fonts?.ready;
    await Promise.all(
      Array.from(wrapper.querySelectorAll("img")).map((image) => {
        if (image.complete) return Promise.resolve();
        return new Promise<void>((resolve) => {
          image.onload = () => resolve();
          image.onerror = () => resolve();
        });
      }),
    );
    await new Promise((resolve) => requestAnimationFrame(resolve));

    const worker = html2pdf()
      .set({
        filename,
        margin: marginInches,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: {
          backgroundColor: "#ffffff",
          scale: 2,
          scrollX: 0,
          scrollY: 0,
          useCORS: true,
          windowWidth: wrapper.scrollWidth,
        },
        jsPDF: {
          unit: "in",
          format: options.pageSize,
          orientation: "portrait",
        },
        pagebreak: { mode: ["css", "legacy"] },
      })
      .from(wrapper)
      .toPdf();

    const pdf: {
      internal: { getNumberOfPages: () => number; pageSize: { getWidth: () => number; getHeight: () => number } };
      setPage: (page: number) => void;
      setFontSize: (size: number) => void;
      setTextColor: (color: string) => void;
      text: (text: string, x: number, y: number, options?: { align: string }) => void;
      output: (type: "arraybuffer") => ArrayBuffer;
    } = await worker.get("pdf");

    if (options.includePageNumbers) {
      const pages = pdf.internal.getNumberOfPages();
      const width = pdf.internal.pageSize.getWidth();
      const height = pdf.internal.pageSize.getHeight();

      for (let page = 1; page <= pages; page += 1) {
        pdf.setPage(page);
        pdf.setFontSize(9);
        pdf.setTextColor("#64748b");
        pdf.text(`${page} / ${pages}`, width / 2, height - 0.28, { align: "center" });
      }
    }

    // Desktop: the WebView won't honour jsPDF's blob-URL `.save()`, so hand the
    // raw bytes to the native Save dialog. Browser: keep the built-in download.
    if (isTauri()) {
      await saveFileNative(new Uint8Array(pdf.output("arraybuffer")), filename, "pdf");
    } else {
      await worker.save();
    }
  } finally {
    stage.remove();
  }
};
